import React from 'react';
import { AlertCircle, Clock, Home } from 'lucide-react';
import OpportunityEmblem from './OpportunityEmblem';
import { addressLine } from '../shared/address';
import { formatDate } from '../kit/time';

// Deadline alerts strip shared by the Board flavors. The flavor supplies
// `groups` — which keys of the /alerts payload to show, in what order, and
// which field on each item holds the date. Items are clickable and hand the
// whole item back through `onOpen` so the flavor decides what "open" means
// (mortgage: opportunity_id → drawer; RE: opportunity_id or transaction_id).
//
//   group: { key, title, icon?, field, idField? }   idField defaults to 'opportunity_id'
//
// `product` ('mortgage' | 'real_estate') is the flavor's product; every item
// gets the matching OpportunityEmblem.
export const MORTGAGE_ALERT_GROUPS = [
  { key: 'rate_lock_expiring', title: 'Rate Lock Expiring', icon: Clock, field: 'expires_at' },
  { key: 'preapproval_expiring', title: 'Preapproval Expiring', icon: Clock, field: 'expires_at' },
  { key: 'closing_soon', title: 'Closing Soon', icon: Home, field: 'closing_date' },
];

const GRID = { 3: 'md:grid-cols-3', 6: 'md:grid-cols-3 lg:grid-cols-6' };

// property_address may be a legacy string or an Address object — render both.
const addr = addressLine;

export default function AlertsStrip({
  alerts, days = 14, labelFor, onOpen, testIdPrefix = 'pipeline',
  groups = MORTGAGE_ALERT_GROUPS, noun = 'alert', product,
}) {
  const total = groups.reduce((n, g) => n + (alerts?.[g.key]?.length || 0), 0);
  if (!alerts || total === 0) return null;
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 shadow-sm mb-6" data-testid={`${testIdPrefix}-alerts`}>
      <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-2">
        <AlertCircle size={16} /> {total} {noun}{total > 1 ? 's' : ''} in next {days} days
      </div>
      <div className={`grid ${GRID[groups.length] || 'md:grid-cols-3'} gap-3`}>
        {groups.map((g) => (
          <AlertGroup key={g.key} group={g} items={alerts[g.key] || []} labelFor={labelFor} onOpen={onOpen} testIdPrefix={testIdPrefix} product={product} />
        ))}
      </div>
    </div>
  );
}

function AlertGroup({ group, items, labelFor, onOpen, testIdPrefix, product }) {
  const Icon = group.icon || Clock;
  const idOf = (i) => i[group.idField || 'opportunity_id'];
  return (
    <div className="bg-white rounded-xl border border-amber-100 p-3">
      <div className="flex items-center gap-2 text-xs text-amber-800 font-medium mb-2">
        <Icon size={12} /> {group.title} ({items.length})
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-slate-400">None</div>
      ) : items.slice(0, 4).map((i) => (
        <button
          key={idOf(i)}
          type="button"
          onClick={() => onOpen?.(i, group)}
          className="flex w-full items-center gap-1.5 text-left text-[11px] text-slate-600 hover:text-[#070B5F] hover:underline"
          data-testid={`${testIdPrefix}-alert-${group.key}-${idOf(i)}`}
        >
          <OpportunityEmblem product={product} size={10} />
          <span className="truncate">{addr(i.property_address) || labelFor?.(i) || idOf(i)} · {formatDate(i[group.field])}</span>
        </button>
      ))}
      {items.length > 4 && <div className="text-[11px] text-slate-400 mt-1">+{items.length - 4} more</div>}
    </div>
  );
}
